import { useState } from 'react';

import axiosClient from '../../../api/axios-client';
import {
    isChallengeExpired,
    isTwoFactorPending,
    LoginSessionResponse,
    TwoFactorPendingResponse,
    VerifyResponse,
} from '../../../api/twoFactor/types';
import { saveTrustedDeviceToken } from '../../../helpers/trustedDeviceStorage';

import { t } from '../../i18n/langue';

interface UseSecondFacteurParams {
    attente: TwoFactorPendingResponse;
    email: string;
    onConnecte: (token: string) => void;
    /** Retour au mot de passe, avec un message si le challenge a expiré. */
    onRetour: (message: string | null) => void;
}

function messageDErreur(erreur: unknown): string {
    const reponse = (erreur as {
        response?: { status?: number; data?: { errors?: Record<string, string[]>; message?: string } };
    })?.response;

    const champs = reponse?.data?.errors;
    const premier = champs ? Object.values(champs)[0]?.[0] : undefined;
    if (premier) return premier;

    // Limite de débit sans message de champ : ce n'est pas le réseau.
    if (reponse?.status === 429) return t('connexion.tropDeTentatives');
    if (reponse) return t('connexion.verificationImpossibleServeur');

    return t('connexion.verificationImpossibleReseau');
}

/**
 * Logique de la double authentification mobile — extraite de l'écran pour
 * rester sous la limite de 200 lignes une fois le code e-mail ajouté au code
 * d'application déjà géré (challenge, enrôlement, code de secours).
 */
export function useSecondFacteur({ attente, email, onConnecte, onRetour }: UseSecondFacteurParams) {
    const [etape, setEtape] = useState(attente);
    const estEnrolement = etape.two_factor === 'enroll';
    const estEmail = etape.method === 'email';

    const [configPrete, setConfigPrete] = useState(false);
    const [code, setCode] = useState('');
    const [codeDeSecours, setCodeDeSecours] = useState(false);
    const [confiance, setConfiance] = useState(false);
    const [erreur, setErreur] = useState<string | null>(null);
    const [enCours, setEnCours] = useState(false);
    const [sessionEnAttente, setSessionEnAttente] = useState<LoginSessionResponse | null>(null);
    const [modeEmail, setModeEmail] = useState(false);
    const [indiceEnrolement, setIndiceEnrolement] = useState<string | null>(null);
    const [renvoiEnCours, setRenvoiEnCours] = useState(false);

    const traiterErreur = (e: unknown) => {
        if (isChallengeExpired(e)) {
            onRetour(messageDErreur(e));
            return;
        }
        setErreur(messageDErreur(e));
        setCode('');
        setEnCours(false);
    };

    const ouvrirSession = (session: LoginSessionResponse) => {
        if (session.trusted_device_token) {
            saveTrustedDeviceToken(email, session.trusted_device_token);
        }
        // Les codes de secours s'affichent AVANT de remettre le jeton : la coque
        // recharge la page dès qu'elle l'a, et ils seraient perdus.
        if (session.recovery_codes?.length) {
            setSessionEnAttente(session);
            setEnCours(false);
            return;
        }
        onConnecte(session.token);
    };

    const soumettre = async () => {
        if (enCours) return;
        setEnCours(true);
        setErreur(null);

        try {
            const { data } = estEnrolement
                ? await axiosClient.post<VerifyResponse>('/login/two-factor/confirm', {
                    challenge_id: etape.challenge_id,
                    code,
                    ...(modeEmail ? { method: 'email' } : {}),
                    remember_device: confiance,
                })
                : await axiosClient.post<VerifyResponse>('/login/two-factor', {
                    challenge_id: etape.challenge_id,
                    ...(codeDeSecours ? { recovery_code: code } : { code }),
                    remember_device: confiance,
                });

            if (isTwoFactorPending(data)) {
                // Super admin en e-mail : bascule vers l'enrôlement de l'application.
                setEtape(data);
                setCode('');
                setConfigPrete(false);
                setEnCours(false);
                return;
            }
            ouvrirSession(data);
        } catch (e) {
            traiterErreur(e);
        }
    };

    const renvoyer = async (redemarrer: () => void) => {
        setRenvoiEnCours(true);
        setErreur(null);
        try {
            await axiosClient.post(modeEmail ? '/login/two-factor/setup/email' : '/login/two-factor/email/resend', {
                challenge_id: etape.challenge_id,
            });
            redemarrer();
        } catch (e) {
            traiterErreur(e);
        } finally {
            setRenvoiEnCours(false);
        }
    };

    const utiliserEmail = async () => {
        setModeEmail(true);
        setErreur(null);
        try {
            const { data } = await axiosClient.post<{ email_hint: string }>('/login/two-factor/setup/email', {
                challenge_id: etape.challenge_id,
            });
            setIndiceEnrolement(data.email_hint);
        } catch (e) {
            traiterErreur(e);
        }
    };

    const basculerCodeDeSecours = () => {
        setCodeDeSecours((v) => !v);
        setCode('');
        setErreur(null);
    };

    const revenirALApplication = () => {
        setModeEmail(false);
        setIndiceEnrolement(null);
        setErreur(null);
    };

    return {
        etape,
        estEnrolement,
        estEmail,
        configPrete,
        setConfigPrete,
        code,
        setCode,
        codeDeSecours,
        confiance,
        setConfiance,
        erreur,
        enCours,
        sessionEnAttente,
        modeEmail,
        indiceEnrolement,
        renvoiEnCours,
        soumettre,
        renvoyer,
        utiliserEmail,
        basculerCodeDeSecours,
        revenirALApplication,
        traiterErreur,
    };
}
